import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import css from './Pagina.module.css';
import { cx } from './ui/cx';

/**
 * Envelope de página do painel: largura máxima, ritmo vertical fixo entre os
 * blocos (24px) e uma entrada suave.
 *
 * Em monitor largo, o espaço extra vira COLUNAS (`Colunas`, `DuasColunas`),
 * não linhas de texto mais compridas — `Prosa` limita o texto corrido.
 */
export function Pagina({
  children,
  largura = 'normal',
}: {
  children: ReactNode;
  /** `larga` para tabelas densas; `estreita` para formulários. */
  largura?: 'normal' | 'larga' | 'estreita';
  /** Legado — ignorado. */
  style?: CSSProperties;
}) {
  return (
    <div
      className={cx(
        css.pagina,
        largura === 'larga' && css.larga,
        largura === 'estreita' && css.estreita,
      )}
    >
      {children}
    </div>
  );
}

/** Cabeçalho padrão: voltar, título, descrição e ações à direita. */
export function TituloPagina({
  titulo,
  descricao,
  acoes,
  voltar,
}: {
  titulo: ReactNode;
  descricao?: ReactNode;
  acoes?: ReactNode;
  /** Link "← Downloads" acima do título, em páginas de detalhe. */
  voltar?: { para: string; rotulo: string };
}) {
  return (
    <header className={css.cabecalho}>
      <div>
        {voltar && (
          <Link to={voltar.para} className={css.voltar}>
            <ArrowLeft aria-hidden="true" />
            {voltar.rotulo}
          </Link>
        )}
        <h1 className={css.titulo}>{titulo}</h1>
        {descricao && <p className={css.descricao}>{descricao}</p>}
      </div>
      {acoes && <div className={css.acoes}>{acoes}</div>}
    </header>
  );
}

/** Grade que decide sozinha quantas colunas cabem (sem media query). */
export function Colunas({
  children,
  minimo = 360,
  gap,
  alinhar,
}: {
  children: ReactNode;
  /** Largura mínima de cada coluna antes de quebrar. */
  minimo?: number;
  gap?: number;
  alinhar?: CSSProperties['alignItems'];
  /** Legado — ignorado. */
  style?: CSSProperties;
}) {
  return (
    <div
      className={css.colunas}
      style={{
        ['--minimo' as string]: `${minimo}px`,
        ...(gap !== undefined ? { ['--gap' as string]: `${gap}px` } : null),
        alignItems: alinhar,
      }}
    >
      {children}
    </div>
  );
}

/**
 * Conteúdo principal e uma lateral fixa (resumo, ajuda); vira uma coluna em
 * telas estreitas.
 */
export function DuasColunas({
  principal,
  lateral,
  lateralPx = 360,
}: {
  principal: ReactNode;
  lateral: ReactNode;
  lateralPx?: number;
  /** Legado — ignorado. */
  gap?: number;
}) {
  return (
    <div className={css.duas} style={{ ['--lateral' as string]: `${lateralPx}px` }}>
      {principal}
      {lateral}
    </div>
  );
}

/** Coluna com espaçamento uniforme entre os filhos. */
export function Pilha({ children, gap }: { children: ReactNode; gap?: number }) {
  return (
    <div
      className={css.pilha}
      style={gap !== undefined ? { ['--gap' as string]: `${gap}px` } : undefined}
    >
      {children}
    </div>
  );
}

/** Texto corrido com largura de leitura confortável. */
export function Prosa({ children }: { children: ReactNode; style?: CSSProperties }) {
  return <div className={css.prosa}>{children}</div>;
}
